/**
 * The forced-choice question bank for the Analyzer onboarding flow.
 *
 * Each question is one revealed-preference probe with mutually-exclusive
 * options. Hand-authored, not LLM-generated — the questions are where The
 * Arena's product voice lives, and LLM-generated alternatives test as
 * plausible-but-flat.
 *
 * Phase 1 ships 30 questions across two domains (ethics + epistemics) — the
 * minimum to test whether the case-base architecture produces a "that's me"
 * agent. Phase 2 expands to ~150 across six domains. Questions are sampled
 * per-session via {@link sampleQuestions}; nothing here is a fixed order.
 *
 * Authoring rules:
 *   1. NO option may be obviously right. Every option must be defensible by
 *      a serious person; the question's value is that smart people disagree.
 *   2. Options must be mutually exclusive — picking one rules the others out.
 *   3. Scenarios should be concrete (a specific situation, not a category).
 *   4. Difficulty is time-to-thoughtful-answer: 1 = under a minute, 3 = the
 *      kind of question you'd stop and stare at the ceiling for.
 *   5. `isShadow` marks anti-aspirational variants — "describe a time you
 *      acted against your stated value here." Used sparingly (1 per ~8 Qs).
 */

export type QuestionDomain =
  | 'ethics'
  | 'epistemics'
  | 'taste'
  | 'decision'
  | 'social'
  | 'professional';

export interface Question {
  /** Stable id — used as the key in OnboardingDraft.stage1.answers. */
  id: string;
  domain: QuestionDomain;
  /** 1 = light (under a minute), 2 = medium, 3 = heavy (real stare). */
  difficulty: 1 | 2 | 3;
  scenario: string;
  /** 3–4 mutually-exclusive options. No "right" answer. */
  options: string[];
  /** True if this is an anti-aspirational shadow question. */
  isShadow?: boolean;
}

// ─── ETHICS (15) ────────────────────────────────────────────────────────────

const ETHICS: Question[] = [
  {
    id: 'eth-hire-cruel-talented',
    domain: 'ethics',
    difficulty: 2,
    scenario:
      "You're hiring for an important role. Candidate A is exceptional at the work but treats subordinates with quiet cruelty. Candidate B is mediocre at the work but everyone wants to work with them.",
    options: [
      'Hire A. Output is what you are paid to produce.',
      'Hire B. Team health compounds over years; raw talent erodes inside it.',
      'Hire A with explicit cultural guardrails and a fast exit if violated.',
      'Hire neither. Keep looking — both downsides are too expensive.',
    ],
  },
  {
    id: 'eth-friend-secret-harm',
    domain: 'ethics',
    difficulty: 3,
    scenario:
      'A close friend tells you something in confidence. If you stay silent, a third person will be seriously harmed. The harm is not certain but is likely.',
    options: [
      'Tell. Preventing harm outranks the friend’s confidentiality.',
      'Press the friend to handle it themselves, with a deadline before you act.',
      'Keep the secret. One breach destroys the trust on which everything else rests.',
      'Tell only the people who can stop the harm; do not broadcast.',
    ],
  },
  {
    id: 'eth-painful-truth-loved-one',
    domain: 'ethics',
    difficulty: 3,
    scenario:
      'You know a truth that would deeply hurt someone you love. They will almost certainly never find out otherwise.',
    options: [
      'Tell them. A relationship resting on a withheld truth is already broken.',
      'Do not tell. The harm of telling exceeds the moral debt of withholding.',
      'Tell them only if they ask directly — until then, let it lie.',
      'Tell them, but choose the timing deliberately (not now, not in anger).',
    ],
  },
  {
    id: 'eth-loyalty-vs-truth',
    domain: 'ethics',
    difficulty: 2,
    scenario:
      'A close friend asks you to back them up in a dispute. You privately think they are in the wrong.',
    options: [
      'Refuse. I will not carry a lie for anyone, including someone I love.',
      'Stay quiet when asked — neither confirm nor deny.',
      'Back them up in the moment, then tell them in private I will not do it again.',
      'Back them up. Loyalty in the moment outranks my private read of who is right.',
    ],
  },
  {
    id: 'eth-help-the-ungrateful-past-wrong',
    domain: 'ethics',
    difficulty: 2,
    scenario:
      'Someone wronged you years ago and never apologized. They now genuinely need your help.',
    options: [
      'Help them as if it never happened. Grudges cost the holder more than the held.',
      'Help, but be honest that the old wound still exists.',
      'Do not help. Reciprocity is moral; ignoring a violation rewards it.',
      'Help them no more than a stranger would get from me.',
    ],
  },
  {
    id: 'eth-distant-strangers-vs-self',
    domain: 'ethics',
    difficulty: 3,
    scenario:
      'You could make a real personal sacrifice (career, time, money) to significantly help people you will never meet.',
    options: [
      'Do it. The math is clear; my comfort does not outweigh their wellbeing.',
      'Do a fraction of it. Sustainable giving beats burnout altruism.',
      'Don’t do it. Closer obligations come first; people depend on me here.',
      'It depends entirely on the cause. Most are not worth it; a few are.',
    ],
  },
  {
    id: 'eth-process-vs-outcome',
    domain: 'ethics',
    difficulty: 2,
    scenario:
      'A clean, predictable process keeps producing some unfair outcomes. A messier process would produce better outcomes but feels arbitrary case-by-case.',
    options: [
      'Stick with the clean process. Rules people can trust beat outcomes judged ad hoc.',
      'Switch to the better-outcomes process. The point of process is good outcomes; lose that and you are worshipping the form.',
      'Patch the clean process to fix the worst cases without abandoning it.',
      'High-stakes cases: prioritize outcome. Low-stakes: prioritize process.',
    ],
  },
  {
    id: 'eth-private-view-public-cost',
    domain: 'ethics',
    difficulty: 2,
    scenario:
      "You hold a view in private that you would not defend in public because of the social cost.",
    options: [
      'That is intellectual cowardice. Defend it or change your mind.',
      'That is just prudence. Some views are not worth the cost of airing.',
      'That is a signal the view might be wrong; if you cannot defend it, examine it.',
      'That is normal — public discourse is broken; honesty belongs in trusted contexts.',
    ],
    isShadow: true,
  },
  {
    id: 'eth-friend-committed-crime',
    domain: 'ethics',
    difficulty: 3,
    scenario:
      'Your closest friend confesses a serious crime to you. The victim is suffering. The friend wants help disappearing.',
    options: [
      'Turn them in. The crime is the crime.',
      'Help them through it but refuse to help them escape consequences.',
      'Help them disappear if their reasons hold up under questioning.',
      'Do nothing — neither help nor turn them in. Walk away.',
    ],
  },
  {
    id: 'eth-forgiveness-without-apology',
    domain: 'ethics',
    difficulty: 2,
    scenario:
      "Someone hurt you significantly. Years later they ask forgiveness — but only because their therapist told them to.",
    options: [
      'Forgive. Forgiveness is for me, not them.',
      'Do not forgive — the request is performative.',
      'Forgive but do not restore the relationship. Different things.',
      'Make them earn it. Forgiveness without restitution is cheap grace.',
    ],
  },
  {
    id: 'eth-canceling-the-reformed',
    domain: 'ethics',
    difficulty: 3,
    scenario:
      'A high performer at your company committed a serious moral failing five years ago. They have since visibly changed and are now indispensable.',
    options: [
      'Fire them. Some things are disqualifying.',
      'Keep them but tell the people they impacted; let those people opt out.',
      'Keep them. People change; permanent records are their own kind of evil.',
      'Depends on the failing. Some you can come back from; some not.',
    ],
  },
  {
    id: 'eth-credit-for-ambiguous-work',
    domain: 'ethics',
    difficulty: 2,
    scenario:
      'You did most of the work on a project. A teammate will present it and get most of the credit unless you correct the record.',
    options: [
      'Correct the record. Honest attribution matters.',
      'Do not correct it. Pettiness about credit costs more than it saves.',
      'Correct it privately to the people whose opinion of you matters.',
      'Mention it once. If it does not land, drop it permanently.',
    ],
  },
  {
    id: 'eth-thankless-help',
    domain: 'ethics',
    difficulty: 1,
    scenario:
      'You have been helping someone who is consistently ungrateful. You expected nothing. You are still bothered.',
    options: [
      'Stop helping. The bother is data; ignore it and you will burn out.',
      'Keep helping at smaller doses. Adjust the level, not the act.',
      'Keep helping at full bore. Gratitude was never the point.',
      'Confront them about the lack of gratitude before deciding.',
    ],
  },
  {
    id: 'eth-truth-that-ends-relationship',
    domain: 'ethics',
    difficulty: 3,
    scenario:
      'A truth you have been hiding from a partner would, if told, almost certainly end the relationship. The reasons it would end it are the reasons it should.',
    options: [
      'Tell them. The relationship is already over; only the formalities remain.',
      'Do not tell. A useful illusion can be more honest than a destructive truth.',
      'Engineer the situation so they discover it themselves.',
      'Tell them only after you have decided whether you want it to end.',
    ],
  },
  {
    id: 'eth-trolley-push',
    domain: 'ethics',
    difficulty: 2,
    scenario:
      'A train will hit five people unless you push one bystander onto the tracks to stop it. The bystander has not consented.',
    options: [
      'Push. The math is the math.',
      'Do not push. Killing as a means is categorically different from letting die.',
      'Would push only if the bystander consented. Consent changes everything.',
      'Refuse the framing — in reality there are always other options.',
    ],
  },
];

// ─── EPISTEMICS (15) ────────────────────────────────────────────────────────

const EPISTEMICS: Question[] = [
  {
    id: 'epi-striking-study-no-time',
    domain: 'epistemics',
    difficulty: 1,
    scenario:
      'You read a study with a striking conclusion. You do not have time to read the paper.',
    options: [
      'Believe it provisionally. Headlines are more right than wrong on average.',
      'Disbelieve it provisionally. Striking results have a high prior of being noise.',
      'Look up whether anyone you trust has weighed in; defer to them.',
      'Mostly ignore it; revisit only if it keeps coming up.',
    ],
  },
  {
    id: 'epi-expert-vs-track-record',
    domain: 'epistemics',
    difficulty: 2,
    scenario:
      'An expert with a bad track record on related calls says X. An amateur with a good track record on related calls says not-X.',
    options: [
      'Trust the amateur. Track record beats credentials, every time.',
      'Trust the expert. Track record on related calls undercounts deep skill.',
      'Look for the underlying argument; whoever has the better one wins.',
      'Do not decide. Wait for a third data point.',
    ],
  },
  {
    id: 'epi-friends-opposite-advice',
    domain: 'epistemics',
    difficulty: 2,
    scenario:
      'Two competent friends give opposite advice on a personal decision. One has aligned incentives with you; one has aligned beliefs with you.',
    options: [
      'Trust the aligned incentives. Their loyalty is the strongest filter.',
      'Trust the aligned beliefs. Shared values predict the call better than shared interests.',
      'Trust whoever explains their reasoning more clearly, regardless of either.',
      'Trust your own gut; the friends were a check, not a decision.',
    ],
  },
  {
    id: 'epi-evidence-vs-social-proof',
    domain: 'epistemics',
    difficulty: 2,
    scenario:
      'You believed X confidently. Strong new evidence says not-X. Most of the people you respect still believe X.',
    options: [
      'Update to not-X publicly. Defer to evidence over social proof.',
      'Update privately for now; wait to see whether the evidence holds up under more eyes.',
      'Stay with X for now. If your respected people have not updated, you are probably missing something.',
      'Reduce confidence in both directions; live in the uncertainty.',
    ],
  },
  {
    id: 'epi-gut-without-words',
    domain: 'epistemics',
    difficulty: 1,
    scenario:
      'You feel strongly that a decision is right but cannot articulate why.',
    options: [
      'Trust the feeling. Intuition compiles experience that words cannot reach.',
      'Do not trust it. If you cannot articulate it, you do not really hold it.',
      'Sleep on it. Re-check tomorrow whether the feeling survives daylight.',
      'Make the decision the feeling points to, but commit to revisit it in three months.',
    ],
  },
  {
    id: 'epi-read-vs-build',
    domain: 'epistemics',
    difficulty: 1,
    scenario:
      'You want to deeply understand something new. You have a month. Two options: read everything serious on it, or build something with it.',
    options: [
      'Read first, then build. Without theory you will build the wrong thing.',
      'Build first, then read. Reading is shallow without a real problem.',
      'Mix them — read in the morning, build in the afternoon, every day.',
      'Skip the month entirely. Talk to three people who already know it.',
    ],
  },
  {
    id: 'epi-contrarian-vs-consensus',
    domain: 'epistemics',
    difficulty: 3,
    scenario:
      'A view you hold is held by ~80% of educated people. A smart contrarian argues against it convincingly.',
    options: [
      'Update. Consensus is mostly noise; a real argument is signal.',
      'Do not update. The strength of the contrarian argument is itself suspicious.',
      'Steelman the contrarian, then check whether the consensus has actually engaged with that steelman.',
      'Hold the view but lower your confidence in it.',
    ],
  },
  {
    id: 'epi-when-to-stop-researching',
    domain: 'epistemics',
    difficulty: 2,
    scenario:
      'You have been researching a decision. You could keep researching for another week or commit now.',
    options: [
      'Commit now. Diminishing returns set in fast.',
      'Research another week. A wrong call costs more than a week.',
      'Set a hard 48-hour deadline; commit at the end of it.',
      'Ask yourself whether you are researching to decide or to avoid deciding.',
    ],
  },
  {
    id: 'epi-source-vs-argument',
    domain: 'epistemics',
    difficulty: 2,
    scenario:
      'The source is unreliable but the argument is sound. The other source is reliable but the argument is weak.',
    options: [
      'Argument over source, always. An idea’s truth is independent of who said it.',
      'Source over argument. Bad sources weave plausible bad arguments; that is the danger.',
      'Both signals matter; their disagreement means the truth is more uncertain than either makes out.',
      'Depends on whether the argument is in the source’s wheelhouse.',
    ],
  },
  {
    id: 'epi-unfalsifiable-theory',
    domain: 'epistemics',
    difficulty: 2,
    scenario:
      'Someone proposes a theory you cannot easily disprove and that explains a lot.',
    options: [
      'Be suspicious. Unfalsifiable theories that explain everything explain nothing.',
      'Take it seriously. Some real theories are hard to disprove early on.',
      'Look for what it predicts that competing theories do not, and check.',
      'Park it. Do not reject, do not accept; revisit when more data arrives.',
    ],
  },
  {
    id: 'epi-friend-heterodox-conclusion',
    domain: 'epistemics',
    difficulty: 2,
    scenario:
      'A friend reasons to a wildly heterodox view. You cannot see the flaw but feel one must be there.',
    options: [
      'Trust the "must be there." Bad conclusions usually have findable flaws.',
      'Do not trust it. The feeling is social discomfort masquerading as logic.',
      'Pause; come back in a week and try harder to find the flaw.',
      'Find someone else smart and ask them to find the flaw.',
    ],
  },
  {
    id: 'epi-default-motives',
    domain: 'epistemics',
    difficulty: 2,
    scenario:
      'You are modeling someone whose motives are ambiguous. What is your default assumption?',
    options: [
      'Assume best motives. You will be wrong sometimes; most people behave well.',
      'Assume worst motives. The cost of naive is greater than the cost of cynical.',
      'Assume the motives the structure incentivizes; people optimize their context.',
      'Do not model the motives; only model the behavior. Motives are noise.',
    ],
  },
  {
    id: 'epi-anecdote-vs-aggregate',
    domain: 'epistemics',
    difficulty: 2,
    scenario:
      'A single vivid anecdote from a trusted source contradicts an aggregate trend.',
    options: [
      'Trust the aggregate. n=1 is always weaker than n=many.',
      'Trust the anecdote in domains the aggregate is bad at measuring (texture, edge cases, recent change).',
      'The anecdote reweights but does not override the aggregate.',
      'Look up the aggregate’s methodology before trusting either.',
    ],
  },
  {
    id: 'epi-two-studies-disagree',
    domain: 'epistemics',
    difficulty: 2,
    scenario:
      'Two well-conducted studies reach opposite conclusions on a question you care about.',
    options: [
      'Believe neither; wait for a meta-analysis or replication.',
      'Look at the methodologies; whichever is more rigorous wins.',
      'Average them and live with the uncertainty.',
      'Pick the one whose conclusion is more useful to act on, but downweight your confidence.',
    ],
  },
  {
    id: 'epi-public-retraction',
    domain: 'epistemics',
    difficulty: 2,
    scenario:
      'You realize you were wrong about something you publicly argued for.',
    options: [
      'Publicly retract, with the specific update. Visible reversals build credibility.',
      'Privately update; making a thing of it draws more attention to the wrong call than it deserves.',
      'Mention the update casually next time the topic comes up; no formal retraction.',
      'Update only if directly challenged; otherwise let your future writing speak.',
    ],
  },
];

export const QUESTION_BANK: Question[] = [...ETHICS, ...EPISTEMICS];

// ─── Sampling ──────────────────────────────────────────────────────────────

/**
 * Sample a session's worth of questions, domain-balanced and difficulty-interleaved.
 *
 * Phase 1 has only 30 questions across 2 domains — `targetCount` typically
 * matches the whole bank. As the bank grows to 150+ across 6 domains, this
 * function does meaningful work: domain-balance the sample, then interleave
 * by difficulty so the user doesn't hit all the heavy questions in a row.
 */
export function sampleQuestions(targetCount: number = 25): Question[] {
  // Group by domain.
  const byDomain = new Map<QuestionDomain, Question[]>();
  for (const q of QUESTION_BANK) {
    const arr = byDomain.get(q.domain) ?? [];
    arr.push(q);
    byDomain.set(q.domain, arr);
  }

  // Shuffle each domain (Fisher–Yates).
  const shuffled = new Map<QuestionDomain, Question[]>();
  for (const [d, arr] of byDomain) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j]!, a[i]!];
    }
    shuffled.set(d, a);
  }

  // Round-robin draw across domains until we hit targetCount.
  const domains = [...shuffled.keys()];
  const out: Question[] = [];
  let cursor = 0;
  while (out.length < targetCount) {
    let progressed = false;
    for (let i = 0; i < domains.length; i++) {
      const d = domains[(cursor + i) % domains.length]!;
      const pool = shuffled.get(d)!;
      const next = pool.shift();
      if (next) {
        out.push(next);
        progressed = true;
        if (out.length >= targetCount) break;
      }
    }
    cursor++;
    if (!progressed) break; // bank exhausted before targetCount
  }

  // Interleave by difficulty: avoid runs of 3+ heavy questions back-to-back.
  // Simple pass — swap any heavy-heavy-heavy run with a lighter question later.
  for (let i = 0; i < out.length - 2; i++) {
    if ((out[i]?.difficulty ?? 0) >= 3 &&
        (out[i + 1]?.difficulty ?? 0) >= 3 &&
        (out[i + 2]?.difficulty ?? 0) >= 3) {
      // find a lighter question later in the array
      for (let j = i + 3; j < out.length; j++) {
        if ((out[j]?.difficulty ?? 0) <= 1) {
          [out[i + 1], out[j]] = [out[j]!, out[i + 1]!];
          break;
        }
      }
    }
  }

  return out;
}

export function questionById(id: string): Question | undefined {
  return QUESTION_BANK.find((q) => q.id === id);
}
